import { HttpClient } from '@adapters/HttpClient';
import {
  Order,
  OrdersApiResponse,
  PreparationLineWithProduct,
  PreparationWithProducts,
} from '@modules/OrderPreparation/PackingV2/models';
import { PreparationType } from '@modules/OrderPreparation/shared/config';
import { USE_MASSIVE_PACKING_MOCKS } from '../config/mocks';
import {
  mockLocationsByProduct,
  mockOrders,
  mockPreparation,
} from '../mocks/data';

/**
 * Respuesta cruda de GET /preparations/{id}.
 * Para type 1 (MASSIVE_PREPARATION) y type 2 (PICK_AND_PACK) el API no envía "lines".
 * "lines" solo viene cuando type === 3 (PICKING).
 * total_orders y buckets siempre vienen.
 */
export interface PreparationApiResponse {
  id: number;
  code: string;
  type: number;
  team_id: number;
  status: number;
  location_id: number | null;
  created_at: string;
  updated_at: string;
  total_orders: number;
  buckets: unknown[];
  lines?: PreparationLineWithProduct[];
}

// 🧩 Tipos para ubicaciones en packing masivo
export interface MassivePackingLocationPayload {
  location_id: number;
  quantity: number;
}

export interface MassivePackingProductPayloadWithLocations {
  product_id: number;
  locations: MassivePackingLocationPayload[];
}

export interface MassivePackingProductPayloadWithoutLocations {
  product_id: number;
  quantity: number;
}

export interface MassivePackingOrderPayloadWithLocations {
  order_id: number;
  products: MassivePackingProductPayloadWithLocations[];
}

export interface MassivePackingOrderPayloadWithoutLocations {
  order_id: number;
  products: MassivePackingProductPayloadWithoutLocations[];
}

export type MassivePackingOrderPayload =
  | MassivePackingOrderPayloadWithLocations
  | MassivePackingOrderPayloadWithoutLocations;

export interface MassivePackingRequestBody {
  team_id: number;
  preparation_id: number;
  orders: MassivePackingOrderPayload[];
}

export interface LocationsByProductApiResponse {
  data: Array<{
    id: number;
    code: string;
    stock: number;
    product_id: number;
    type?: {
      id: number;
      name: string;
    } | null;
  }>;
  meta?: any;
}

/**
 * Adapter de API para la pantalla de Packing Masivo.
 * Encapsula todas las llamadas necesarias al backend.
 */
export class MassivePackingApiAdapter {
  constructor(private httpClient: HttpClient) {}

  // 🎯 Obtener una preparación por ID.
  // Para type 1 (MASSIVE_PREPARATION) el API no envía "lines"; normalizamos a lines: [].
  async getPreparation(preparationId: number): Promise<PreparationWithProducts> {
    if (USE_MASSIVE_PACKING_MOCKS) {
      return { ...mockPreparation, id: preparationId };
    }

    const raw =
      await this.httpClient.get<PreparationApiResponse>(`/preparations/${preparationId}`);

    // Validar tipo de preparación: solo permitir MASSIVE_PACKING (type === 1)
    if (raw.type !== PreparationType.MASSIVE_PACKING) {
      throw new Error('La preparación no es de tipo packing masivo');
    }

    // Para type 1 y 2 el API no incluye "lines"; asegurar siempre un array para el contrato PreparationWithProducts
    const preparation: PreparationWithProducts = {
      ...raw,
      lines: raw.lines ?? [],
    };

    return preparation;
  }

  // 🎯 Obtener todas las órdenes asociadas a una preparación
  async getOrdersByPreparationId(preparationId: number): Promise<Order[]> {
    if (USE_MASSIVE_PACKING_MOCKS) {
      return mockOrders;
    }

    const response = await this.httpClient.get<OrdersApiResponse>('/orders', {
      preparation_ids: String(preparationId),
      'sorting[column]': 'id',
      'sorting[direction]': 'asc',
      per_page: '9999',
    });

    return response.data || [];
  }

  // 🎯 Obtener ubicaciones con stock para un conjunto de productos (batch)
  async getLocationsByProductIds(
    productIds: number[],
  ): Promise<Record<number, LocationsByProductApiResponse['data']>> {
    if (!productIds.length) {
      return {};
    }

    if (USE_MASSIVE_PACKING_MOCKS) {
      const result: Record<number, LocationsByProductApiResponse['data']> = {};
      productIds.forEach((pid) => {
        if (mockLocationsByProduct[pid]) {
          result[pid] = mockLocationsByProduct[pid];
        }
      });
      return result;
    }

    const params = {
      product_ids: productIds.join(','),
      per_page: '1000',
      order_by: 'id',
      order_direction: 'asc',
    };

    const raw = await this.httpClient.get<LocationsByProductApiResponse>('/locations', params);
    const list = Array.isArray((raw as any).data) ? (raw as any).data : (raw as any) || [];

    const byProduct: Record<number, LocationsByProductApiResponse['data']> = {};

    list.forEach((loc: any) => {
      const productId: number | undefined =
        typeof loc.product_id === 'number' ? loc.product_id : undefined;

      if (!productId) return;

      if (!byProduct[productId]) {
        byProduct[productId] = [];
      }

      byProduct[productId].push({
        id: loc.id,
        code: loc.code,
        stock: typeof loc.stock === 'number' ? loc.stock : 0,
        product_id: productId,
        type: loc.type || null,
      });
    });

    return byProduct;
  }

  /**
   * Obtener enlace para imprimir/descargar etiqueta de envío de una orden.
   * GET /orders/{id}/print?printing_type=pdf → { link: string }
   */
  async getOrderPrintLink(
    orderId: number,
    printingType: 'pdf' | 'printnode' = 'pdf',
    printerCode?: string,
  ): Promise<any> {
    const params: Record<string, string> = {
      printing_type: printingType,
    };
    if (printingType === 'printnode' && printerCode) {
      params.printer_code = printerCode;
    }

    return this.httpClient.get<any>(`/orders/${orderId}/print`, params);
  }

  // 🎯 Ejecutar el packing masivo
  async massivePack(
    preparationId: number,
    body: MassivePackingRequestBody,
  ): Promise<any> {
    if (USE_MASSIVE_PACKING_MOCKS) {
      return Promise.resolve({ success: true });
    }

    return this.httpClient.put(
      `/preparations/massive/${preparationId}/pack`,
      body,
    );
  }

  /**
   * Cancelar una preparación de packing masivo.
   * PUT /preparations/{preparationId}/cancel
   * Respuesta correcta: 204 No Content (sin cuerpo).
   */
  async cancelPreparation(preparationId: number): Promise<void> {
    if (USE_MASSIVE_PACKING_MOCKS) {
      return Promise.resolve();
    }

    await this.httpClient.put(`/preparations/${preparationId}/cancel`, {});
  }
}

